import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ArrowRight, Download } from 'lucide-react';
import { appealsDashboardAPI, type DashRichFilterParams } from '../../services/api';
import { buildCsv, downloadCsv } from '../../utils/csv';
import { Modal, SearchBox } from './DashboardPrimitives';
import { BlockLoader } from '../loading';
import { useTableSort, SortTh } from './useTableSort';
import DrillLink from './DrillLink';
import { DRILL_ROUTES, buildDrillUrl } from '../../utils/drilldown';
import { useHeldPage } from '../../navigation/useHeldPage';
import { todayInIndia } from '../../pages/home/homeNumbers';
import StatTile from './StatTile';
import { useDashboardList } from './useDashboardList';
import { APPEALS_LIST, decideLink, type DecideLink } from './lists/appeals';

/**
 * Which appeal system a row came from. The dashboard unions red_flag_appeals and
 * yellow_flag_appeals, and the two are owned by SEPARATE report pages — so every
 * drill-down must be routed by kind, not sent to one default page.
 */
type FlagKind = 'red' | 'yellow';

const appealRouteFor = (kind: FlagKind | undefined): string =>
  kind === 'yellow' ? DRILL_ROUTES.yellowAppeals : DRILL_ROUTES.appeals;

const kindLabel = (kind: FlagKind | undefined): string => (kind === 'yellow' ? 'Yellow Flag Appeals' : 'Red Flags Appeals');

/** Small red/yellow pill so a mixed list is readable at a glance. */
function KindBadge({ kind }: { kind: FlagKind | undefined }) {
  const yellow = kind === 'yellow';
  return (
    <span
      className={`ml-1.5 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
        yellow ? 'bg-amber-50 text-amber-700' : 'bg-rose-50 text-rose-700'
      }`}
    >
      {yellow ? 'Yellow' : 'Red'}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Types (mirror dashboard-appeals.controller)

type AppealStatus = 'pending' | 'under_review' | 'cancelled' | 'rejected';

interface Kpis {
  total: number;
  open: number;
  resolved: number;
  overturned: number;
  rejected: number;
  overturnPct: number | null;
  avgTatMs: number | null;
  avgTatLabel: string | null;
}
interface Outcomes { pending: number; under_review: number; cancelled: number; rejected: number }
interface ByType { flagType: string; flagKind: FlagKind; label: string; total: number; overturned: number; rejected: number; open: number; overturnPct: number | null }
/** The metric set the manager table repeats per flag kind. */
interface ManagerTally { total: number; overturned: number; rejected: number; open: number; overturnPct: number | null }

interface ByManager {
  managerCode: string | null; managerName: string;
  /** Combined across both systems — drives row sorting. */
  total: number; overturned: number; rejected: number; open: number; overturnPct: number | null;
  /** The same metrics, split by system. Each links to the report that owns it. */
  red: ManagerTally;
  yellow: ManagerTally;
}
interface AppealRow {
  id: number | string;
  /** Which appeal system authored this row (red_flag_appeals / yellow_flag_appeals). */
  flagKind: FlagKind;
  /** `${flagKind}:${id}` — `id` alone repeats across the two tables. */
  rowUid: string;
  employee_code: string;
  employee: string;
  flagType: string;
  flagLabel: string;
  status: AppealStatus;
  managerCode: string | null;
  managerName: string | null;
  store: string;
  city: string;
  eventDate: string | null;
  createdAt: string | null;
  decidedAt: string | null;
  tatMs: number | null;
  tatLabel: string | null;
}
interface OpenRow extends AppealRow { ageMs: number | null; ageLabel: string | null; ageDays: number | null }
interface DashData {
  month: { ym: string; label: string; startDate: string; endDate: string };
  kpis: Kpis;
  outcomes: Outcomes;
  byFlagType: ByType[];
  byManager: ByManager[];
  rows: AppealRow[];
  openQueue: { total: number; rows: OpenRow[] };
}

// ─────────────────────────────────────────────────────────────────────────────
// Status taxonomy — cancelled = flag removed (overturned), rejected = maintained.

const STATUS_META: Record<AppealStatus, { label: string; hex: string }> = {
  cancelled: { label: 'Overturned', hex: '#10b981' },
  rejected: { label: 'Rejected', hex: '#64748b' },
  under_review: { label: 'Under review', hex: '#3b82f6' },
  pending: { label: 'Pending', hex: '#f59e0b' },
};
const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

function StatusBadge({ status }: { status: AppealStatus }) {
  const m = STATUS_META[status] ?? { label: String(status || 'Unknown'), hex: '#94a3b8' };
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium" style={{ backgroundColor: `${m.hex}1a`, color: m.hex }}>
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: m.hex }} />
      {m.label}
    </span>
  );
}

/** A pop-up list of appeals: its title, its rows, and what the list is told to draw beside them. */
interface AppealList {
  title: string;
  /** An open queue's rows carry how long each has waited as well (see `waiting`). */
  rows: Array<AppealRow & Partial<Pick<OpenRow, 'ageMs' | 'ageLabel'>>>;
  /** The list mixes red and yellow, so each row says which it is. */
  showKind?: boolean;
  /**
   * The buttons to the pages where these appeals are decided, one for each page the person
   * holds. A list, not one button: "All open appeals" holds both kinds, and red and yellow
   * appeals are decided on different pages.
   */
  decide?: DecideLink[];
  /**
   * An open queue: nothing on it is decided, so no row has a TAT. Its last column is how
   * long each appeal has waited instead, under "Open for" as on the queue's own card — the
   * wait the line that opened the list has just named.
   */
  waiting?: boolean;
}

/** The decide buttons a list carries: those of the pages the person holds. */
const heldLinks = (...links: Array<DecideLink | null>): DecideLink[] =>
  links.filter((l): l is DecideLink => l !== null);

// ─────────────────────────────────────────────────────────────────────────────

export default function AppealsTab({ projectId, month, roleId, filters }: { projectId: number | null; month: string; roleId?: string; filters?: DashRichFilterParams }) {
  const [data, setData] = useState<DashData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqRef = useRef(0);

  // Drill-down modal (appeals filtered by flag type or manager)
  const [drill, setDrill] = useState<AppealList | null>(null);
  const [mgrOpen, setMgrOpen] = useState(false);
  // The lists a headline number opens (raised, resolved, the two open queues, and both
  // queues together for the home screen's number) open by address (?list=), and what is
  // open is read from it on every render. Held in state it would be shut by the reload
  // that follows the default Role filter.
  const { list, openList, closeList } = useDashboardList();
  // The pages where appeals are decided, when this person holds them.
  const redPage = useHeldPage(DRILL_ROUTES.appeals);
  const yellowPage = useHeldPage(DRILL_ROUTES.yellowAppeals);

  useEffect(() => {
    const reqId = ++reqRef.current;
    setLoading(true);
    setError(null);
    appealsDashboardAPI
      .getDashboard({ month, projectId: projectId ?? undefined, roleId: roleId || undefined, ...filters })
      .then((res) => {
        if (reqId !== reqRef.current) return;
        setData((res.data?.data ?? null) as DashData | null);
      })
      .catch((err) => {
        if (reqId !== reqRef.current) return;
        setError(err.response?.data?.message || 'Failed to load appeals dashboard');
        setData(null);
      })
      .finally(() => {
        if (reqId === reqRef.current) setLoading(false);
      });
  }, [month, projectId, roleId, filters]);

  const k = data?.kpis;

  // The open queue is split per flag kind and each half sorts itself inside
  // OpenQueue, so no sort hook is needed here. Rows arrive oldest-first from the
  // server, and that order is preserved by the split.
  const openQueueRows = data?.openQueue.rows ?? [];
  const openRed = useMemo(() => openQueueRows.filter((r) => r.flagKind !== 'yellow'), [openQueueRows]);
  const openYellow = useMemo(() => openQueueRows.filter((r) => r.flagKind === 'yellow'), [openQueueRows]);
  // The month's appeals that have been decided: the rows behind the Resolved number.
  const resolvedRows = useMemo(
    () => (data?.rows ?? []).filter((r) => r.status === 'cancelled' || r.status === 'rejected'),
    [data],
  );

  // India's date: the end of the window Yellow Flag Appeals is opened on.
  const today = todayInIndia();

  // ── CSV download (full appeals list for the month) ──
  const handleDownload = () => {
    if (!data) return;
    // 'Flag Kind' is required now the export mixes both systems: the old header
    // said "Red Flag" for every row, which mislabels yellow-flag appeals — and a
    // red and a yellow reason can share a label, making them indistinguishable.
    const headers = ['Employee Code', 'Employee', 'Flag Kind', 'Flag', 'Status', 'Manager', 'Store', 'City', 'Event Date', 'Raised On', 'Decided On', 'Resolution TAT'];
    const rows = data.rows.map((r) => [
      r.employee_code,
      r.employee,
      r.flagKind === 'yellow' ? 'Yellow' : 'Red',
      r.flagLabel,
      STATUS_META[r.status]?.label ?? String(r.status),
      r.managerName ?? '',
      r.store,
      r.city,
      r.eventDate ?? '',
      r.createdAt ?? '',
      r.decidedAt ?? '',
      r.tatLabel ?? '',
    ]);
    downloadCsv(`Appeals_${data.month.ym}.csv`, buildCsv(headers, rows));
  };
  const hasAppeals = (data?.rows.length ?? 0) > 0;

  if (loading) {
    return (
      <BlockLoader className="h-72" />
    );
  }
  if (error) return <div className="flex h-40 items-center justify-center text-sm text-rose-600">{error}</div>;
  if (!data) return <div className="flex h-40 items-center justify-center text-sm text-gray-500">No appeals data.</div>;

  const monthLabel = data.month.label;
  const mgrTop = data.byManager.slice(0, 8);

  // The list the address names, drawn from the rows on screen now. Each is the list a
  // headline number opens, so its rows are exactly what that number counts.
  // "All open appeals" is every row of the open queue: the rows the home screen's Appeals
  // number counts. It is the server's own array, never red then yellow joined, so the
  // oldest appeal heads it whichever kind it is. This dashboard shows the two kinds apart
  // (they are decided on different pages), so this list opens only by address. A kind's
  // button is offered only when that kind has an appeal waiting.
  const addressList: AppealList | null =
    list === APPEALS_LIST.raised ? { title: `Appeals raised in ${monthLabel}`, rows: data.rows, showKind: true }
    : list === APPEALS_LIST.resolved ? { title: `Resolved appeals raised in ${monthLabel}`, rows: resolvedRows, showKind: true }
    : list === APPEALS_LIST.openRed ? { title: 'Open red-flag appeals', rows: openRed, waiting: true, decide: heldLinks(decideLink('red', redPage, openRed, today)) }
    : list === APPEALS_LIST.openYellow ? { title: 'Open yellow-flag appeals', rows: openYellow, waiting: true, decide: heldLinks(decideLink('yellow', yellowPage, openYellow, today)) }
    : list === APPEALS_LIST.open ? {
      title: 'All open appeals',
      rows: openQueueRows,
      showKind: true,
      waiting: true,
      decide: heldLinks(
        openRed.length > 0 ? decideLink('red', redPage, openRed, today) : null,
        openYellow.length > 0 ? decideLink('yellow', yellowPage, openYellow, today) : null,
      ),
    }
    : null;
  // A number opens its list only when above zero and equal to the rows the list will show.
  const raisedTotal = k?.total ?? 0;
  const resolvedTotal = k?.resolved ?? 0;
  const openRaised = raisedTotal > 0 && raisedTotal === data.rows.length ? () => openList(APPEALS_LIST.raised) : undefined;
  const openResolved = resolvedTotal > 0 && resolvedTotal === resolvedRows.length ? () => openList(APPEALS_LIST.resolved) : undefined;

  return (
    <div className="space-y-4">
      {/* ─── Header + download ─── */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900">Appeals overview</h2>
          <p className="text-xs text-gray-500">Red-flag and yellow-flag appeals raised in {monthLabel}</p>
        </div>
        <button
          type="button"
          onClick={handleDownload}
          disabled={!hasAppeals}
          className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-primary-200 bg-white px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" /> Download
        </button>
      </div>

      {/* ─── KPI strip ─── */}
      {/* The counts open the list behind them. "Open" is two tiles, one per kind, because
          it is two lists: red and yellow appeals are decided by different people on
          different pages. (The home screen's one Appeals number opens both together, as
          "All open appeals", which carries a button to each page.) Unlike the tiles beside
          them, they count every month, and say so. The rate and the TAT are not sets of
          appeals, so they stay plain.
          Six across only from the 2xl width, three across below it: at six across on a
          1280px window the Resolved tile's helper runs to a second line, and a wrapped
          line sets the tiles' words at two heights. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 2xl:grid-cols-6">
        <StatTile label="Appeals raised" value={String(raisedTotal)} helper={monthLabel} onClick={openRaised} />
        <StatTile label="Overturn rate" value={k?.overturnPct == null ? '—' : `${k.overturnPct}%`} valueTone="good" helper={`${k?.overturned ?? 0} of ${k?.resolved ?? 0} resolved`} />
        <StatTile label="Avg resolution TAT" value={k?.avgTatLabel ?? '—'} helper="raised → decided" />
        <StatTile
          label="Open red-flag"
          value={String(openRed.length)}
          valueTone={openRed.length > 0 ? 'bad' : 'default'}
          helper="undecided, any month"
          onClick={openRed.length > 0 ? () => openList(APPEALS_LIST.openRed) : undefined}
        />
        <StatTile
          label="Open yellow-flag"
          value={String(openYellow.length)}
          valueTone={openYellow.length > 0 ? 'bad' : 'default'}
          helper="undecided, any month"
          onClick={openYellow.length > 0 ? () => openList(APPEALS_LIST.openYellow) : undefined}
        />
        <StatTile label="Resolved" value={String(resolvedTotal)} helper={`${k?.overturned ?? 0} overturned · ${k?.rejected ?? 0} rejected`} onClick={openResolved} />
      </div>

      {/* ─── Appeals by flag type — red and yellow side by side ─── */}
      {/* Two panels rather than one blended chart: red and yellow flags are raised
          under different rules and decided by different reviewers, so stacking
          their parameters in one axis invites a comparison that is not meaningful.
          Side by side keeps each readable while the shared bar scale is per-panel
          (a project with 48 red and 2 yellow appeals would otherwise render the
          yellow panel as invisible slivers). */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <FlagTypeChart
          kind="red"
          types={data.byFlagType.filter((t) => t.flagKind !== 'yellow')}
          onPick={(t) => setDrill({
            title: `Appeals — ${t.label}`,
            rows: data.rows.filter((r) => r.flagType === t.flagType && r.flagKind !== 'yellow'),
          })}
        />
        <FlagTypeChart
          kind="yellow"
          types={data.byFlagType.filter((t) => t.flagKind === 'yellow')}
          onPick={(t) => setDrill({
            title: `Appeals — ${t.label}`,
            rows: data.rows.filter((r) => r.flagType === t.flagType && r.flagKind === 'yellow'),
          })}
        />
      </div>

      {/* ─── By manager ─── */}
      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-2 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Appeals by manager</h3>
            <p className="text-xs text-gray-500">Whose team is appealing most in {monthLabel}</p>
          </div>
          {data.byManager.length > 8 && (
            <button type="button" onClick={() => setMgrOpen(true)} className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50">
              View All <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {data.byManager.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500">No appeals this month.</div>
        ) : (
          <ManagerTable rows={mgrTop} drillRange={data.month} onRowClick={(m) => setDrill({ title: `Appeals — ${m.managerName}`, rows: data.rows.filter((r) => (r.managerCode || '—') === (m.managerCode || '—')), showKind: true })} />
        )}
      </section>

      {/* ─── Open appeals — one queue per flag kind, side by side ─── */}
      {/* Split because these are two separate ACTION QUEUES: red and yellow appeals
          are decided by different reviewers on different pages, so a blended
          oldest-first list forces each reviewer to scan past rows they cannot act
          on. Each side keeps its own "oldest first" ordering and its own top-10,
          so a large red backlog can no longer bury every yellow item. */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <OpenQueue kind="red" rows={openRed} onViewAll={() => openList(APPEALS_LIST.openRed)} />
        <OpenQueue kind="yellow" rows={openYellow} onViewAll={() => openList(APPEALS_LIST.openYellow)} />
      </div>

      {/* Drill-down modal (appeals of one flag type / manager) */}
      {drill && createPortal(<AppealListModal list={drill} onClose={() => setDrill(null)} />, document.body)}
      {/* The list the address names; closing takes the name out of the address. */}
      {addressList && createPortal(<AppealListModal list={addressList} onClose={closeList} />, document.body)}
      {/* View-all: managers */}
      {mgrOpen && createPortal(
        <Modal title="Appeals by manager" onClose={() => setMgrOpen(false)}>
          <ManagerTable rows={data.byManager} drillRange={data.month} onRowClick={(m) => { setMgrOpen(false); setDrill({ title: `Appeals — ${m.managerName}`, rows: data.rows.filter((r) => (r.managerCode || '—') === (m.managerCode || '—')), showKind: true }); }} />
        </Modal>,
        document.body,
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * "Appeals by <kind> flag" — standing columns, one per appealed parameter,
 * height ∝ appeal COUNT and stacked by decision status.
 *
 * Rendered once per flag kind. The bar scale is deliberately LOCAL to each panel:
 * a shared scale across both would squash the rarer kind into invisible slivers
 * (48 red vs 2 yellow appeals is a realistic split), and the panels answer
 * "which parameter dominates within this system", not "which system is bigger".
 */
function FlagTypeChart({ kind, types, onPick }: {
  kind: FlagKind;
  types: ByType[];
  onPick: (t: ByType) => void;
}) {
  const yellow = kind === 'yellow';
  const title = yellow ? 'Appeals by yellow flag' : 'Appeals by red flag';

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <KindBadge kind={kind} />
      </div>
      <p className="text-xs text-gray-500">Most-appealed parameters and how they were decided</p>

      {types.length === 0 ? (
        // Distinguish "none this month" from "this system isn't in use here" —
        // yellow flags are manually raised, so an empty panel is normal, not broken.
        <div className="py-10 text-center text-sm text-gray-400">
          No {yellow ? 'yellow' : 'red'}-flag appeals this month.
        </div>
      ) : (() => {
        const maxTotal = Math.max(1, ...types.map((t) => t.total));
        const H = 190; // px — tallest column
        return (
          <div className="mt-5">
            <div className="flex h-[210px] items-end gap-3 border-b border-gray-200 px-1">
              {types.map((t) => {
                // Floor of 6px so a count of 1 stays visible beside a tall column.
                const barPx = t.total > 0 ? Math.max(6, Math.round((t.total / maxTotal) * H)) : 0;
                const seg = (n: number, hex: string, label: string) =>
                  n > 0 ? <div style={{ height: `${(n / t.total) * 100}%`, backgroundColor: hex }} title={`${label}: ${n}`} /> : null;
                return (
                  <button
                    key={`${t.flagKind}:${t.flagType}`}
                    type="button"
                    onClick={() => onPick(t)}
                    className="group flex h-full flex-1 flex-col items-center justify-end"
                    title={`${t.label}: ${t.total}`}
                  >
                    <div className="mb-1 text-sm font-semibold tabular-nums text-gray-900">{t.total}</div>
                    <div
                      className="flex w-full max-w-[56px] flex-col-reverse overflow-hidden rounded-t-md transition-opacity group-hover:opacity-90"
                      style={{ height: `${barPx}px` }}
                    >
                      {seg(t.overturned, STATUS_META.cancelled.hex, 'Overturned')}
                      {seg(t.rejected, STATUS_META.rejected.hex, 'Rejected')}
                      {seg(t.open, STATUS_META.pending.hex, 'Open')}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="flex gap-3 px-1 pt-2">
              {types.map((t) => (
                <div key={`${t.flagKind}:${t.flagType}`} className="flex-1 text-center text-[11px] leading-tight text-gray-500">
                  {t.label}
                  {t.overturnPct != null && <div className="text-emerald-600">{t.overturnPct}% overturned</div>}
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500">
              <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: STATUS_META.cancelled.hex }} />Overturned</span>
              <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: STATUS_META.rejected.hex }} />Rejected</span>
              <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: STATUS_META.pending.hex }} />Open</span>
            </div>
          </div>
        );
      })()}
    </section>
  );
}

/**
 * One flag kind's open-appeals queue — the SLA list for whoever decides that kind.
 *
 * Sorting lives here rather than in the parent so each queue sorts independently:
 * re-ordering the red backlog must not disturb the yellow one, and a hook shared
 * across both could not express two orderings at once.
 */
function OpenQueue({ kind, rows, onViewAll }: {
  kind: FlagKind;
  rows: OpenRow[];
  onViewAll: () => void;
}) {
  const yellow = kind === 'yellow';
  const sort = useTableSort(rows, {
    employee: (r) => r.employee,
    flag: (r) => r.flagLabel,
    manager: (r) => r.managerName,
    age: (r) => r.ageMs,
  });
  const top = sort.sorted.slice(0, 10);

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900">
              Open {yellow ? 'yellow' : 'red'}-flag appeals
            </h3>
            <KindBadge kind={kind} />
          </div>
          <p className="text-xs text-gray-500">Awaiting a decision — oldest first</p>
        </div>
        {rows.length > 10 && (
          <button type="button" onClick={onViewAll} className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50">
            View All <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {top.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-400">
          No open {yellow ? 'yellow' : 'red'}-flag appeals — all caught up.
        </div>
      ) : (
        <div className="overflow-x-auto">
          {/* Narrower than the old combined table: these sit two-up, and the
              per-row KindBadge is gone because the whole panel is one kind. */}
          <table className="w-full min-w-[520px] text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-400">
                <SortTh label="Employee" sortKey="employee" sort={sort} className="pb-3 font-semibold" />
                <SortTh label={yellow ? 'Reason' : 'Red flag'} sortKey="flag" sort={sort} className="pb-3 pl-4 font-semibold" />
                <SortTh label="Manager" sortKey="manager" sort={sort} className="pb-3 pl-4 font-semibold" />
                <SortTh label="Open for" sortKey="age" sort={sort} align="right" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {top.map((r) => (
                <tr key={r.rowUid} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="py-3 font-medium text-gray-900">
                    {/* Drill to this employee's appeals on the page that OWNS this
                        kind. Deliberately NO date window: this queue is not
                        month-scoped (dashboard-appeals.controller.ts lists every
                        still-undecided appeal, however old), so imposing the
                        selected month would hide the row just clicked. */}
                    {r.employee_code ? (
                      <DrillLink
                        href={buildDrillUrl(appealRouteFor(r.flagKind), { employeeCode: r.employee_code })}
                        title={`View ${r.employee}'s appeals in ${kindLabel(r.flagKind)}`}
                      >
                        {r.employee}
                      </DrillLink>
                    ) : (
                      r.employee
                    )}
                    {r.employee_code && <span className="ml-1 text-xs text-gray-400">({r.employee_code})</span>}
                    <div className="mt-0.5 text-xs font-normal text-gray-500">{[r.store, r.city].filter(Boolean).join(' • ') || '—'}</div>
                  </td>
                  <td className="py-3 pl-4 text-gray-700">{r.flagLabel}</td>
                  <td className="py-3 pl-4 text-gray-700">{r.managerName || '—'}</td>
                  <td className="whitespace-nowrap py-3 pl-4 text-right font-semibold text-rose-500">{r.ageLabel ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 10 && <div className="mt-2 text-right text-xs text-gray-400">Top 10 of {rows.length}</div>}
        </div>
      )}
    </section>
  );
}

function ManagerTable({ rows, onRowClick, drillRange }: {
  rows: ByManager[];
  onRowClick: (m: ByManager) => void;
  /** Server's month window, forwarded to the report so the drill-down reproduces
   *  exactly the appeals this table counted. */
  drillRange?: { startDate: string; endDate: string; label?: string };
}) {
  const sort = useTableSort(rows, {
    manager: (m) => m.managerName,
    total: (m) => m.total,
    rTotal: (m) => m.red.total,
    rOverturned: (m) => m.red.overturned,
    rOpen: (m) => m.red.open,
    rPct: (m) => m.red.overturnPct,
    yTotal: (m) => m.yellow.total,
    yOverturned: (m) => m.yellow.overturned,
    yOpen: (m) => m.yellow.open,
    yPct: (m) => m.yellow.overturnPct,
  });

  /** A per-kind count that drills into the report owning that kind. Only the
   *  APPEALS count links — the sub-metrics (overturned/open) have no corresponding
   *  filter on either report page, so linking them would land on a list the number
   *  cannot be read off. Plain text is honest; a link that ignores the constraint
   *  it implies is not. */
  const countCell = (m: ByManager, kind: FlagKind) => {
    const t = kind === 'yellow' ? m.yellow : m.red;
    if (t.total === 0) return <span className="text-gray-300">—</span>;
    if (!m.managerCode || m.managerCode === '—') return <span className="font-semibold text-gray-900">{t.total}</span>;
    return (
      <DrillLink
        href={buildDrillUrl(kind === 'yellow' ? DRILL_ROUTES.yellowAppeals : DRILL_ROUTES.appeals, {
          managerCode: m.managerCode,
          startDate: drillRange?.startDate,
          endDate: drillRange?.endDate,
        })}
        title={`View ${m.managerName}'s ${t.total} ${kind}-flag appeal(s) raised in ${drillRange?.label ?? 'this month'}`}
        showIcon={false}
        className="font-semibold"
      >
        {t.total}
      </DrillLink>
    );
  };

  const pctCell = (v: number | null) => (v == null ? <span className="text-gray-300">—</span> : `${v}%`);

  return (
    <div className="overflow-x-auto">
      {/* Wider min-width than before: the table now carries the same four metrics
          twice. The grouped header row is what keeps that readable — without it
          eight numeric columns are indistinguishable at a glance. */}
      <table className="w-full min-w-[900px] text-sm">
        <thead>
          {/* Grouping row. Spans are load-bearing: 1 (manager) + 4 (red) + 4 (yellow)
              must equal the 9 columns in the row below, or every header shifts. */}
          <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400">
            <th className="pb-1" />
            <th className="pb-1 pl-4 text-center font-semibold text-rose-600" colSpan={4}>Red flag</th>
            <th className="pb-1 pl-4 text-center font-semibold text-amber-600" colSpan={4}>Yellow flag</th>
          </tr>
          <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-400">
            <SortTh label="Manager" sortKey="manager" sort={sort} className="pb-3 font-semibold" />
            <SortTh label="Appeals" sortKey="rTotal" sort={sort} align="right" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
            <SortTh label="Overturned" sortKey="rOverturned" sort={sort} align="right" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
            <SortTh label="Open" sortKey="rOpen" sort={sort} align="right" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
            <SortTh label="Overturn %" sortKey="rPct" sort={sort} align="right" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
            <SortTh label="Appeals" sortKey="yTotal" sort={sort} align="right" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
            <SortTh label="Overturned" sortKey="yOverturned" sort={sort} align="right" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
            <SortTh label="Open" sortKey="yOpen" sort={sort} align="right" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
            <SortTh label="Overturn %" sortKey="yPct" sort={sort} align="right" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
          </tr>
        </thead>
        <tbody>
          {sort.sorted.map((m) => (
            <tr key={m.managerCode || m.managerName} className="cursor-pointer border-b border-gray-50 hover:bg-gray-50/50" onClick={() => onRowClick(m)}>
              {/* The manager cell is plain text now — the drill-downs moved onto
                  the per-kind APPEALS counts, where the number and the list it
                  opens are the same thing. */}
              <td className="py-3 font-medium text-gray-900">
                {m.managerName}
                {m.managerCode && m.managerCode !== '—' && (
                  <span className="ml-1 text-xs font-normal text-gray-400">({m.managerCode})</span>
                )}
              </td>
              <td className="py-3 pl-4 text-right">{countCell(m, 'red')}</td>
              <td className="py-3 pl-4 text-right text-emerald-600">{m.red.total ? m.red.overturned : <span className="text-gray-300">—</span>}</td>
              <td className="py-3 pl-4 text-right text-amber-600">{m.red.total ? m.red.open : <span className="text-gray-300">—</span>}</td>
              <td className="py-3 pl-4 text-right text-gray-700">{pctCell(m.red.overturnPct)}</td>
              <td className="py-3 pl-4 text-right">{countCell(m, 'yellow')}</td>
              <td className="py-3 pl-4 text-right text-emerald-600">{m.yellow.total ? m.yellow.overturned : <span className="text-gray-300">—</span>}</td>
              <td className="py-3 pl-4 text-right text-amber-600">{m.yellow.total ? m.yellow.open : <span className="text-gray-300">—</span>}</td>
              <td className="py-3 pl-4 text-right text-gray-700">{pctCell(m.yellow.overturnPct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AppealListModal({ list, onClose }: { list: AppealList; onClose: () => void }) {
  const { title, rows, showKind, decide, waiting } = list;
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.employee, r.employee_code, r.flagLabel, r.managerName, r.store, r.city].some((v) => (v || '').toLowerCase().includes(q)));
  }, [rows, search]);
  const sort = useTableSort(filtered, {
    employee: (r) => r.employee,
    flag: (r) => r.flagLabel,
    manager: (r) => r.managerName,
    status: (r) => r.status,
    raised: (r) => r.createdAt,
    // The last column, whichever it is on this list.
    tat: (r) => (waiting ? r.ageMs : r.tatMs),
  });
  return (
    <Modal title={title} onClose={onClose}>
      <div className="mb-3">
        <SearchBox value={search} onChange={setSearch} placeholder="Search by employee, flag, manager, store…" />
      </div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-gray-500">{filtered.length} of {rows.length} appeals</span>
        {/* Nothing is decided on this dashboard: these go to the pages where it is. They
            carry no number, because those pages do not open on these same rows. Held
            together so the count stays on the left and the buttons on the right. */}
        {decide && decide.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {decide.map((d) => (
              <Link
                key={d.kind}
                to={d.to}
                className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50"
              >
                {d.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        )}
      </div>
      {filtered.length === 0 ? (
        // An open queue with nothing in it is good news, not a search that found nothing:
        // the home screen's "0 waiting for a decision" lands here.
        <div className="py-10 text-center text-sm text-gray-500">
          {waiting && rows.length === 0 && !search.trim() ? 'Nothing is waiting for a decision.' : 'No matching appeals.'}
        </div>
      ) : (
        <div className="max-h-[55vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-400">
                <SortTh label="Employee" sortKey="employee" sort={sort} className="pb-3 font-semibold" />
                {/* "Flag", not "Red flag": the same list shows yellow-flag appeals too. */}
                <SortTh label="Flag" sortKey="flag" sort={sort} className="pb-3 pl-4 font-semibold" />
                <SortTh label="Manager" sortKey="manager" sort={sort} className="pb-3 pl-4 font-semibold" />
                <SortTh label="Status" sortKey="status" sort={sort} className="pb-3 pl-4 font-semibold" />
                <SortTh label="Raised" sortKey="raised" sort={sort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                <SortTh label={waiting ? 'Open for' : 'TAT'} sortKey="tat" sort={sort} align="right" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {sort.sorted.map((r) => (
                <tr key={r.rowUid} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="py-3 font-medium text-gray-900">
                    {r.employee}
                    <span className="ml-1 text-xs text-gray-400">({r.employee_code})</span>
                    <div className="mt-0.5 text-xs font-normal text-gray-500">{[r.store, r.city].filter(Boolean).join(' • ') || '—'}</div>
                  </td>
                  <td className="py-3 pl-4 text-gray-700">
                    {r.flagLabel}
                    {showKind && <KindBadge kind={r.flagKind} />}
                  </td>
                  <td className="py-3 pl-4 text-gray-700">{r.managerName || '—'}</td>
                  <td className="py-3 pl-4"><StatusBadge status={r.status} /></td>
                  <td className="whitespace-nowrap py-3 pl-4 text-gray-600">{fmtDate(r.createdAt)}</td>
                  <td className="whitespace-nowrap py-3 pl-4 text-right text-gray-700">{(waiting ? r.ageLabel : r.tatLabel) ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
